import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MagnifyingGlass, ArrowElbowDownLeft, CaretRight,
  ChartLine, Plugs, Play, CalendarBlank, ListBullets,
  Globe, ChatCircle, Gear, Warning, ArrowsLeftRight,
  TreeStructure, Pulse, Database, Hexagon, Broadcast,
  BookOpen, Heartbeat, Table, EnvelopeSimple, Waveform,
  CreditCard,
} from '@phosphor-icons/react';

const COMMANDS = [
  { id: 'dashboard', label: 'Dashboard', path: '/dashboard', icon: ChartLine, group: 'Navigate' },
  { id: 'connections', label: 'Connections', path: '/connections', icon: Plugs, group: 'Navigate' },
  { id: 'executor', label: 'Executor', path: '/executor', icon: Play, group: 'Navigate' },
  { id: 'scheduler', label: 'Scheduler', path: '/scheduler', icon: CalendarBlank, group: 'Navigate' },
  { id: 'logs', label: 'Logs', path: '/logs', icon: ListBullets, group: 'Navigate' },
  { id: 'alerts', label: 'Alerts', path: '/alerts', icon: Warning, group: 'Navigate' },
  { id: 'webhooks', label: 'Webhooks', path: '/webhooks', icon: Globe, group: 'Navigate' },
  { id: 'nl-query', label: 'NL Query', path: '/nl', icon: ChatCircle, group: 'Navigate' },
  { id: 'query', label: 'Query Engine', path: '/query', icon: MagnifyingGlass, group: 'Navigate' },
  { id: 'voice', label: 'Voice Control', path: '/voice', icon: Waveform, group: 'Navigate' },
  { id: 'map', label: 'Environment Map', path: '/map', icon: TreeStructure, group: 'Navigate' },
  { id: 'matr', label: 'MATR Dashboard', path: '/matr', icon: Pulse, group: 'Navigate' },
  { id: 'transforms', label: 'Transforms', path: '/transforms', icon: ArrowsLeftRight, group: 'Navigate' },
  { id: 'retention', label: 'Retention', path: '/retention', icon: Database, group: 'Navigate' },
  { id: 'dictionary', label: 'Data Dictionary', path: '/data-dictionary', icon: Table, group: 'Navigate' },
  { id: 'constellation', label: 'Constellation', path: '/constellation', icon: Hexagon, group: 'Navigate' },
  { id: 'signals', label: 'Signal Exchange', path: '/signals', icon: Broadcast, group: 'Navigate' },
  { id: 'commons', label: 'MERA Commons', path: '/commons', icon: BookOpen, group: 'Navigate' },
  { id: 'heatcheck', label: 'Heat Check', path: '/heatcheck', icon: Heartbeat, group: 'Navigate' },
  { id: 'digest', label: 'Daily Digest', path: '/digest', icon: EnvelopeSimple, group: 'Navigate' },
  { id: 'subscription', label: 'Subscription', path: '/subscription', icon: CreditCard, group: 'Navigate' },
  { id: 'settings', label: 'Settings', path: '/settings', icon: Gear, group: 'Navigate' },
];

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Listen for Cmd+K / Ctrl+K
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(prev => !prev);
      }
      if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  // Focus input when open
  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const filtered = useMemo(() => {
    if (!query.trim()) return COMMANDS;
    const q = query.toLowerCase();
    return COMMANDS.filter(c =>
      c.label.toLowerCase().includes(q) ||
      c.id.includes(q) ||
      c.group.toLowerCase().includes(q)
    );
  }, [query]);

  // Reset selection when filtered list changes
  useEffect(() => { setSelectedIndex(0); }, [filtered]);

  const executeCommand = useCallback((cmd) => {
    if (cmd.path) navigate(cmd.path);
    setOpen(false);
  }, [navigate]);

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && filtered[selectedIndex]) {
      executeCommand(filtered[selectedIndex]);
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.1 }}
        className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh] bg-black/60 backdrop-blur-sm"
        onClick={() => setOpen(false)}
        data-testid="command-palette-overlay"
      >
        <motion.div
          initial={{ opacity: 0, y: -10, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -10, scale: 0.98 }}
          transition={{ duration: 0.15 }}
          className="w-full max-w-lg bg-kova-card border border-kova-cyan/12 shadow-2xl overflow-hidden"
          onClick={e => e.stopPropagation()}
          data-testid="command-palette"
          role="dialog"
          aria-label="Command palette"
        >
          {/* Search Input */}
          <div className="flex items-center gap-3 px-4 border-b border-kova-cyan/12">
            <MagnifyingGlass size={16} className="text-kova-slate flex-shrink-0" />
            <input
              ref={inputRef}
              data-testid="command-palette-input"
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search pages, commands..."
              className="flex-1 bg-transparent text-kova-white font-mono text-xs py-3 placeholder:text-kova-slate focus:outline-none"
              aria-label="Search commands"
            />
            <kbd className="font-mono text-[8px] text-kova-slate border border-kova-cyan/12 px-1.5 py-0.5">ESC</kbd>
          </div>

          {/* Results */}
          <div className="max-h-72 overflow-y-auto py-1" role="listbox">
            {filtered.length === 0 ? (
              <div className="px-4 py-6 text-center text-kova-slate font-mono text-xs">No results found</div>
            ) : (
              filtered.map((cmd, i) => (
                <button
                  key={cmd.id}
                  onClick={() => executeCommand(cmd)}
                  onMouseEnter={() => setSelectedIndex(i)}
                  role="option"
                  aria-selected={i === selectedIndex}
                  data-testid={`cmd-${cmd.id}`}
                  className={`w-full flex items-center gap-3 px-4 py-2 text-left transition-colors ${
                    i === selectedIndex
                      ? 'bg-kova-cyan/10 text-kova-white'
                      : 'text-kova-silv hover:bg-kova-card2'
                  }`}
                >
                  <cmd.icon size={14} className={i === selectedIndex ? 'text-kova-cyan' : 'text-kova-slate'} />
                  <span className="font-mono text-[11px] tracking-wide flex-1">{cmd.label}</span>
                  {i === selectedIndex && (
                    <ArrowElbowDownLeft size={12} className="text-kova-cyan" />
                  )}
                </button>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center gap-4 px-4 py-2 border-t border-kova-cyan/12">
            <span className="flex items-center gap-1 font-mono text-[8px] text-kova-slate">
              <kbd className="border border-kova-cyan/12 px-1 py-px">↑↓</kbd> Navigate
            </span>
            <span className="flex items-center gap-1 font-mono text-[8px] text-kova-slate">
              <kbd className="border border-kova-cyan/12 px-1 py-px">↵</kbd> Open
            </span>
            <span className="flex items-center gap-1 font-mono text-[8px] text-kova-slate">
              <kbd className="border border-kova-cyan/12 px-1 py-px">Esc</kbd> Close
            </span>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
